import { useEffect, useState } from 'react';
import { AlertBox, Box, FormInputGroup, FormLabel, H2, Typography } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { useBriefFormContext } from '../../../../BriefContext/BriefFormContext';
import { Field_ClientConfiguredField_Fragment } from '../../../brief.generated';
import { ClientConfiguredFieldProps } from '../types';
import DateSelector from './DateSelector';
import TimeSelector from './TimeSelector';
import { useUpdateDateField } from './useUpdateDateField';

// clientConfiguredValue is in the following format: '2023-08-14T00:00:00Z'
export function Input({
    id,
    clientConfiguredValue,
    setValue,
    register,
    immutable,
}: ClientConfiguredFieldProps) {
    const { locale } = usePage();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Convert the clientConfiguredValue to the user's local timezone and format it as an ISO date string (YYYY-MM-DD)
    const localIsoTime = clientConfiguredValue
        ? new Date(clientConfiguredValue).toLocaleDateString('en-CA', {
              timeZone: timezone,
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
          })
        : null;
    const [date, setDateDispatcher] = useState<string | null>(localIsoTime);
    const [time, setTime] = useState(clientConfiguredValue);
    const { briefId } = useBriefFormContext();
    const updateMutation = useUpdateDateField(id, briefId, time);

    const setDate = (newDate: string | null) => {
        // Only clear time when the user picks a different date, not when re-selecting a time in the same date
        const isSameDay = date && newDate && date === newDate;
        if (!isSameDay) {
            setTime('');
        }
        setDateDispatcher(newDate);
    };

    const displayDateObj = time ? new Date(time) : null;

    useEffect(() => {
        register(id, {
            required: __('Please select a date and time', {
                _context:
                    'Validation error message when user has not selected a date and time for an appointment',
            }),
            validate: (value) => {
                const valid = !!value && !isNaN(new Date(value).getTime());
                return valid || __('Please select a date and time');
            },
        });
    }, [id, register]);

    useEffect(() => {
        let shouldValidate = false;
        if (time) {
            // Revalidate so the form error state can be updated
            // This is required so review modal can reset the error state selection change
            shouldValidate = true;
            updateMutation(time);
        }
        setValue(id, time, { shouldValidate });
    }, [id, time, setValue, updateMutation]);

    return (
        <Box pb={8} data-testid={`field-${id}`}>
            <H2 fontSkin="title-subsection">
                {__('Choose a date and time', {
                    _context:
                        'Heading for question on a form for customer to make an appointment with a designer',
                })}
            </H2>
            <FormLabel fontSkin="body-standard-bold" htmlFor={id} marginBottom={'3'}>
                {__('Your timezone')}
            </FormLabel>
            <Typography fontSize="small">{timezone}</Typography>
            <div>
                <Box marginTop="6" data-testid={`field-${id}-date-selector`}>
                    <FormLabel fontSkin="body-standard-bold" htmlFor={id} marginBottom={'3'}>
                        {__('Select a date', {
                            _context: 'Label for a date selector input on a form',
                        })}
                    </FormLabel>
                    <FormInputGroup>
                        <DateSelector
                            timezone={timezone}
                            setDate={setDate}
                            date={date}
                            immutable={immutable}
                        />
                    </FormInputGroup>
                </Box>
                {date && isDateFormattedCorrectly(date) && (
                    <Box marginTop="7" data-testid={`field-${id}-time-selector`}>
                        <FormLabel fontSkin="body-standard-bold" htmlFor={id} marginBottom={'3'}>
                            {__('Select a time', {
                                _context: 'Label for a time selector input on a form',
                            })}
                        </FormLabel>
                        <TimeSelector
                            timezone={timezone}
                            register={register}
                            id={id}
                            time={time}
                            setTime={setTime}
                            date={date}
                            immutable={immutable}
                        />
                    </Box>
                )}
            </div>
            {displayDateObj && (
                <AlertBox style={{ width: '100%' }} skin="success" marginTop="7">
                    <Typography fontSkin="body-standard" fontSize="small">
                        {__('Your DesignLive session will be scheduled for')}
                    </Typography>
                    <Box mt="5">
                        <Typography fontWeight="bold" as="span">
                            {displayDateObj.toLocaleDateString(locale, {
                                dateStyle: 'full',
                                timeZone: timezone,
                            })}
                        </Typography>
                        <Typography
                            fontSkin="body-standard-bold"
                            marginLeft="2"
                            as="span"
                            data-testid={`field-${id}-appointment-time-display`}
                        >
                            {displayDateObj.toLocaleTimeString('default', {
                                hour: 'numeric',
                                minute: '2-digit',
                                timeZoneName: 'short',
                                hourCycle: 'h12',
                                timeZone: timezone,
                            })}
                        </Typography>
                        <Typography fontSkin="body-standard" fontSize="small" mt="6">
                            {__(`You'll be able to reschedule after payment if you need to.`)}
                        </Typography>
                    </Box>
                </AlertBox>
            )}
        </Box>
    );
}

export function Review({ clientConfiguredValue: time, id }: Field_ClientConfiguredField_Fragment) {
    const dateObj = new Date(time);

    return (
        <div data-testid={`field-readonly-${id}`}>
            {time && (
                <AlertBox style={{ width: '100%' }} skin="info">
                    <Typography fontSkin="body-standard" fontSize="small">
                        {__('Your DesignLive session will be scheduled for')}
                    </Typography>
                    <Box mt="5">
                        <Typography fontWeight="bold" as="span">
                            {dateObj.toLocaleDateString('default', {
                                dateStyle: 'full',
                            })}
                        </Typography>
                        <Typography fontSkin="body-standard-bold" marginLeft="2" as="span">
                            {dateObj.toLocaleTimeString('default', {
                                hour: 'numeric',
                                minute: '2-digit',
                                timeZoneName: 'short',
                                hourCycle: 'h12',
                            })}
                        </Typography>
                        <Typography fontSkin="body-standard" fontSize="small" mt="6">
                            {__(`You'll be able to reschedule after payment if you need to.`)}
                        </Typography>
                    </Box>
                </AlertBox>
            )}
        </div>
    );
}

// Check if the date is in the format YYYY-MM-DD
function isDateFormattedCorrectly(date: string) {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    return regex.test(date);
}
